<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.COUPON.CREATE_OR_UPDATE.SHOW"
    :handler="(data) => openEditor(data)"
  />

  <DialogComponent ref="modal" type="modal" noInnerPadding hideCloseButton>
    <div class="w-full h-full flex flex-col overflow-auto p-4 bg-slate-50">
      <div class="mobile-status-padding"></div>

      <!-- HEADER -->
      <div class="flex justify-between items-center mb-6">
        <button
          class="w-12 h-12 p-3 rounded-full bg-slate-200"
          @click="() => $refs.modal.hide()"
        >
          <ColorImg
            class="w-full h-full bg-primary -rotate-90"
            src="/images/all/arrow-up.svg"
          />
        </button>
        <h2 class="text-2xl font-semibold text-primary">
          {{ isEditing ? 'Modifier le coupon' : 'Nouveau coupon' }}
        </h2>
        <div class="w-12"></div>
      </div>

      <!-- FORM -->
      <div class="flex-1 space-y-4">
        <!-- TITLE (hidden) -->
        <input v-model="form.title" type="hidden" />
        <!-- DESCRIPTION (hidden) -->
        <input v-model="form.description" type="hidden" />

        <!-- IMAGE UPLOADER -->
        <div class="bg-white rounded-xl p-4">
          <label class="block mb-3 text-sm font-medium text-gray-700">
            Image du coupon
          </label>
          <FileUploadWidget
            :defaultUrls="form.pictureUrl ? [form.pictureUrl] : []"
            :acceptedFiles="['image']"
            :maxFiles="1"
            maxFileSize="3mo"
            @change="onImageChange"
          />
        </div>

        <!-- COUPON CODE -->
        <div class="bg-white rounded-xl p-4">
          <label class="block mb-2 text-sm font-medium text-gray-700">
            Code coupon
          </label>
          <input
            v-model="form.code"
            class="inputComponent w-full font-mono uppercase"
            placeholder="EX: BETWIN2024"
            maxlength="20"
          />
          <p class="text-xs text-gray-500 mt-1">
            Le code que le client devra utiliser
          </p>
        </div>

        <!-- PREMIUM TOGGLE -->
        <div class="bg-white rounded-xl p-4">
          <label class="block mb-3 text-sm font-medium text-gray-700">
            Type de coupon
          </label>
          <div class="flex gap-4">
            <button
              @click="form.isPremium = false"
              :class="[
                'flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all',
                !form.isPremium
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-200 bg-gray-50 text-gray-600'
              ]"
            >
              Standard
            </button>
            <button
              @click="form.isPremium = true"
              :class="[
                'flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all',
                form.isPremium
                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                  : 'border-gray-200 bg-gray-50 text-gray-600'
              ]"
            >
              <span class="flex items-center justify-center gap-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Premium
              </span>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-2">
            Les coupons premium nécessitent un abonnement pour être utilisés
          </p>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="grid grid-cols-3 gap-3 mt-6 pt-4 border-t border-gray-200">
        <button
          v-if="isEditing"
          class="px-6 py-3 rounded-xl bg-red-100 text-red-600 font-medium hover:bg-red-200"
          @click="deleteCoupon"
        >
          Supprimer
        </button>
        <button
          :class="{ 'col-span-2': !isEditing }"
          class="px-6 py-3 rounded-xl bg-gray-200 text-gray-700 font-medium"
          @click="() => $refs.modal.hide()"
        >
          Annuler
        </button>
        <button
          class="px-6 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700"
          @click="submit"
        >
          {{ isEditing ? 'Mettre à jour' : 'Créer' }}
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script>
export default {
  name: 'CouponEditor',

  data() {
    return {
      isEditing: false,
      form: {
        id: null,
        title: '',
        description: '',
        pictureUrl: '',
        code: '',
        isPremium: false
      }
    };
  },

  methods: {
    openEditor(coupon) {
      if (coupon) {
        this.isEditing = true;
        this.form = {
          id: coupon.id,
          title: coupon.title || '',
          description: coupon.description || '',
          pictureUrl: coupon.pictureUrl || '',
          code: coupon.code || '',
          isPremium: coupon.isPremium || false
        };
      } else {
        this.isEditing = false;
        this.form = {
          id: null,
          title: '',
          description: '',
          pictureUrl: '',
          code: '',
          isPremium: false
        };
      }
      this.$refs.modal.show();
    },

    onImageChange(url) {
      this.form.pictureUrl = url;
    },

    submit() {
      const payload = this.isEditing
        ? { ...this.form, action: 'update' }
        : { ...this.form, action: 'create' };

      this.$bus.emit(this.$constants.EVENTS.COUPON.SUBMIT, payload);
      this.$refs.modal.hide();
    },

    deleteCoupon() {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce coupon ?')) {
        this.$bus.emit(this.$constants.EVENTS.COUPON.DELETE, this.form);
        this.$refs.modal.hide();
      }
    }
  }
};
</script>
